import React from "react";
import { motion } from "framer-motion";
import { Check, Clock, MapPin, Sparkles } from "lucide-react";
import { useAddToChat } from "../../../../components/chat/chatContext";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nbaDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nba-dash.pt-br";
import { useNavigate } from "react-router-dom";
import MobileNBAScoreGrid from "./mobile/MobileNBAScoreGrid";
import PredictButton from "../../../../components/dashboards/components/shared/PredictButton";
import XPicksButton from "../../../../components/dashboards/components/shared/XPicksButton";
import ViewOddsButton from "../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../components/dashboards/components/shared/PredictionBox";

const NBADesktopGameCard = ({
  game,
  onViewOdds,
  isSelected = false,
  isFrozen = false,
  onToggleSelection = null,
  prediction = null
}) => {
  const isBrazilian = getBrazilianStatus();
  const t = (text) => (isBrazilian ? nbaDashPtBR[text] || text : text);

  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  if (!game) return null;

  const { teams, scores, status, date, arena } = game;

  const handleAnalyzeInChat = async () => {
    if (!prediction) return;

    const message = `${t('Analyze this NBA prediction:')}

    ${teams?.visitors?.name} @ ${teams?.home?.name}
    
    ${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
    ${t('Confidence Level:')} ${prediction.confidence}
    ${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}
    
    ${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  const statusShort = status?.short;
  const isLive = statusShort === 2;
  const isFinished = statusShort === 3;
  const isUpcoming = statusShort === 1;

  const getStatusDisplay = () => {
    if (isLive) {
      const currentPeriod = game.periods?.current;
      if (currentPeriod) {
        const periodDisplay = currentPeriod > 4 ? t('OT') : `${t('Q')}${currentPeriod}`;
        if (status?.clock) {
          return `${periodDisplay} ${status.clock}`;
        }
        return `${t('LIVE')} ${periodDisplay}`;
      }
      return t('LIVE');
    }
    if (isFinished) return t('FINAL');
    return null;
  };

  const formatGameTime = () => {
    if (isLive) return t('LIVE');
    if (isFinished) return t('Final');

    if (date?.start) {
      try {
        const gameDate = new Date(date.start);

        if (!isNaN(gameDate.getTime())) {
          const options = {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/New_York'
          };
          return `${gameDate.toLocaleTimeString('en-US', options)} ${t('ET')}`;
        }
      } catch (e) {
        // Fall through
      }
    }

    return t('TBD');
  };

  const statusDisplay = getStatusDisplay();

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={`relative p-5 rounded-sm transition-all duration-200 ${
        isSelected
          ? 'bg-blue-500/20 border border-blue-500/30'
          : 'bg-white/[0.03] border border-white/[0.08] hover:border-white/40'
      }`}
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-4 right-4 z-10">
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${
            isLive ? 'bg-red-500' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}

      {/* League Label */}
      <div className="text-[11px] text-white/65 font-semibold uppercase tracking-wider mb-3 truncate pr-20">
        {game.league?.name || game.league || t('NBA')}
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2.5 mt-4">
        {/* Visitors Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {teams?.visitors?.logo && (
              <img
                src={teams.visitors.logo}
                alt={teams.visitors.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40 flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-base font-mono text-white tracking-tight leading-tight truncate">
              {teams?.visitors?.name || t('Visitors')}
            </span>
          </div>
          <span className="text-3xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.visitors?.points ?? '–') : '–'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            {teams?.home?.logo && (
              <img
                src={teams.home.logo}
                alt={teams.home.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40 flex-shrink-0"
                loading="lazy"
              />
            )}
            <span className="text-base font-mono text-white tracking-tight leading-tight truncate">
              {teams?.home?.name || t('Home')}
            </span>
          </div>
          <span className="text-3xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.home?.points ?? '–') : '–'}
          </span>
        </div>
      </div>

      {/* Game Time and Venue - only for upcoming games */}
      {!isFinished && !isLive && (
        <div className="mt-3 pt-2 border-t border-white/10">
          <div className="flex items-center justify-center gap-1.5 text-white/50">
            <Clock size={12} />
            <span className="text-[11px] font-medium">
              {formatGameTime()}
            </span>
            {arena?.name && (
              <>
                <span className="text-white/30">•</span>
                <MapPin size={12} />
                <span className="text-[11px] font-medium truncate max-w-[200px]">
                  {arena.name}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Prediction Result (if available) */}
      {prediction && (
        <PredictionBox
          prediction={prediction}
          onAnalyze={handleAnalyzeInChat}
          variant="desktop"
          className="mt-4"
          t={t}
        />
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.visitors?.name,
                homeTeamId: teams?.home?.id,
                visitorsTeamId: teams?.visitors?.id,
                leagueId: game.league?.id || 12,
                date: date?.start,
                time: formatGameTime(),
                gameId: game?.id
              }}
              sport="nba"
              label="XPICKS"
            />
          </div>
        )}

        {/* Prediction Button */}
        {(isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={
                isFrozen
                  ? t('COOLDOWN')
                  : isSelected
                  ? t('SELECTED')
                  : isLive
                  ? t('PREDICT LIVE')
                  : t('PREDICT')
              }
              icon={isFrozen ? Clock : isSelected ? Check : Sparkles}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(game);
                }
              }}
            />
          </div>
        )}

        {/* View Odds Button */}
        {/* Disabled until integration with the API to show odds
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label={t('VIEW ODDS')}
              icon={Eye}
              onClick={() => onViewOdds(game)}
            />
          </div>
        )}
        */}
      </div>

      {/* Quarter Scores for Live/Finished Games */}
      {(isLive || isFinished) && scores?.visitors?.linescore && scores?.home?.linescore && (
        <MobileNBAScoreGrid teams={teams} scores={scores} t={t} />
      )}
    </motion.div>
  );
};

export default NBADesktopGameCard;
